import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

import { useExternalRegisterImport } from "./externalRegisterImportProvider";
import { MetricChip, StageUnavailable, WorkflowCard } from "../shared/ExternalRegisterSettingsUi";

export const CommitReportStage = () => {
  const { t } = useTranslation();
  const {
    latestCommitResponse,
    validateResponse,
    partitionDownloads,
  } = useExternalRegisterImport();
  const progressPercent = latestCommitResponse
    ? Math.round((latestCommitResponse.currentBatch / Math.max(1, latestCommitResponse.totalBatches)) * 100)
    : 0;
  const isCommitRunning = latestCommitResponse?.status === "committing";

  return (
    <WorkflowCard title={t("externalRegister.import.report.title")}>
      {latestCommitResponse ? (
        <Stack spacing={2}>
          {latestCommitResponse.simulated && (
            <Alert severity="info" variant="outlined">
              {t("externalRegister.import.report.simulationAlert")}
            </Alert>
          )}
          <Stack direction="row" spacing={1.25} alignItems="center">
            {isCommitRunning && <CircularProgress size={18} thickness={5} aria-label={t("externalRegister.import.report.progress.ariaLabel")} />}
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t("externalRegister.import.report.progress.batchProgress", {
                current: latestCommitResponse.currentBatch,
                total: latestCommitResponse.totalBatches,
                percent: progressPercent,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("externalRegister.import.report.progress.batchSize", { size: latestCommitResponse.batchSize })}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <MetricChip label={t("externalRegister.import.report.metrics.committed")} value={latestCommitResponse.successfullyImported} />
            <MetricChip label={t("externalRegister.import.report.metrics.failed")} value={latestCommitResponse.errors} />
            <MetricChip label={t("externalRegister.import.report.metrics.retryable")} value={latestCommitResponse.retryableRows} />
            <MetricChip label={t("externalRegister.import.report.metrics.status")} value={latestCommitResponse.status} />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("externalRegister.import.report.table.headers.batch")}</TableCell>
                  <TableCell>{t("externalRegister.import.report.table.headers.status")}</TableCell>
                  <TableCell>{t("externalRegister.import.report.table.headers.attempted")}</TableCell>
                  <TableCell>{t("externalRegister.import.report.table.headers.committed")}</TableCell>
                  <TableCell>{t("externalRegister.import.report.table.headers.failed")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {latestCommitResponse.batches.map((batch) => (
                  <TableRow key={batch.batchNumber}>
                    <TableCell>{batch.batchNumber}</TableCell>
                    <TableCell>{batch.status}</TableCell>
                    <TableCell>{batch.attemptedRows}</TableCell>
                    <TableCell>{batch.committedRows}</TableCell>
                    <TableCell>{batch.failedRows}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Typography variant="h6">{t("externalRegister.import.report.downloads.title")}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {partitionDownloads.map((download) => (
              <Button
                key={download.kind}
                href={download.url}
                download={download.fileName}
                startIcon={<DownloadIcon />}
                variant="outlined"
              >
                {t("externalRegister.import.report.downloads.buttonLabel", { kind: download.kind, count: download.rowCount })}
              </Button>
            ))}
          </Stack>
        </Stack>
      ) : (
        <StageUnavailable message={validateResponse ? t("externalRegister.import.report.unavailable.noReview") : t("externalRegister.import.report.unavailable.noValidation")} />
      )}
    </WorkflowCard>
  );
};
